/**
 * Servicio de sucursales — Portal Cliente Menuee
 *
 * Responsabilidad: lectura de sucursales y config_sucursal (políticas operativas).
 *
 * Consume: supabaseClient.
 *
 * Qué nunca debe hacer:
 * - CRUD de sucursales (solo Admin).
 * - Calcular monto oficial de delivery (preview en utils/deliveryTarifa; oficial en E6).
 * - Contener lógica visual ni JSX.
 * - Transformar filas para UI (los componentes reciben datos vía hooks en fases posteriores).
 */

import { supabase } from "../supabaseClient";

const SELECT_SUCURSAL_BASICO = `
  id,
  local_id,
  nombre,
  direccion,
  horario_atencion,
  horario_reparto,
  horarios,
  logo_url,
  activo,
  latitud,
  longitud
`;

const SELECT_CONFIG_SUCURSAL = `
  sucursal_id,
  delivery_activo,
  delivery_base,
  delivery_gratis_desde,
  propina_porcentaje_sugerido
`;

const configPorSucursal = new Map();

/**
 * Obtiene una sucursal activa por ID.
 *
 * @param {string} id
 * @returns {Promise<Object>}
 */
export async function obtenerSucursalPorId(id) {
  const { data, error } = await supabase
    .from("sucursales")
    .select(SELECT_SUCURSAL_BASICO)
    .eq("id", id)
    .eq("activo", true)
    .single();

  if (error) {
    throw error;
  }
  return data;
}

/**
 * Lista sucursales activas del tenant (RLS aplica filtro por local_id).
 *
 * @returns {Promise<Object[]>}
 */
export async function listarSucursalesActivas() {
  const { data, error } = await supabase
    .from("sucursales")
    .select(SELECT_SUCURSAL_BASICO)
    .eq("activo", true)
    .order("nombre");

  if (error) {
    throw error;
  }
  return data ?? [];
}

/**
 * Sucursales activas de un local ya resuelto por slug.
 *
 * @param {string} localId
 * @returns {Promise<Object[]>}
 */
export async function listarSucursalesDelPortal(localId) {
  if (!localId) {
    return [];
  }

  const { data, error } = await supabase
    .from("sucursales")
    .select(SELECT_SUCURSAL_BASICO)
    .eq("activo", true)
    .eq("local_id", localId)
    .order("nombre");

  if (error) {
    throw error;
  }
  return data ?? [];
}

/**
 * Obtiene configuración operativa de una sucursal (delivery, propina sugerida).
 * Usada por deliveryTarifa.js para preview y por checkout en fases posteriores.
 *
 * @param {string} sucursalId
 * @returns {Promise<Object|null>}
 */
export async function obtenerConfiguracionSucursal(sucursalId) {
  if (!sucursalId) {
    return null;
  }

  const existente = configPorSucursal.get(sucursalId);
  if (existente) {
    return existente;
  }

  const pending = (async () => {
    const { data, error } = await supabase
      .from("config_sucursal")
      .select(SELECT_CONFIG_SUCURSAL)
      .eq("sucursal_id", sucursalId)
      .maybeSingle();

    if (error) {
      throw error;
    }
    return data;
  })().catch((error) => {
    configPorSucursal.delete(sucursalId);
    throw error;
  });

  configPorSucursal.set(sucursalId, pending);
  return pending;
}
